import { TableColumnType, TableProps } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import TableComponent from 'components/Table';
import { useEffect, useMemo, useState } from 'react';
import type {
  FieldMeta,
  FieldsConfig,
  TableFieldSortConfig,
} from '../../shared/types';
import FieldRenderer from './FieldRenderer';

interface TableRecord<T = Record<string, any>> {
  id: string;
  createdTime: string;
  fields: T;
}

interface SalesTableProps {
  records: TableRecord[];
  fieldMeta: Record<string, FieldMeta> | null;
  loading: boolean;
  fieldsToRender: FieldsConfig;
  pagination?: TableProps['pagination'];
}

const createFieldMetaMap = (fieldMeta: Record<string, FieldMeta> | null) => {
  if (!fieldMeta) return {};

  return Object.values(fieldMeta).reduce(
    (acc, curr) => ({
      ...acc,
      [curr.id]: curr,
    }),
    {} as Record<string, FieldMeta>,
  );
};

const sortFieldsByRenderOrder = (
  fields: [string, FieldMeta][],
  fieldsToRender: FieldsConfig,
) => {
  return fields?.sort(([keyA], [keyB]) => {
    const indexA = Object.keys(fieldsToRender)?.indexOf(keyA);
    const indexB = Object.keys(fieldsToRender)?.indexOf(keyB);
    return indexA - indexB;
  });
};

const createSorterWithConfig = (
  fieldSortConfig: TableFieldSortConfig,
  key: string,
) => {
  if (!fieldSortConfig) {
    return false;
  }

  if (fieldSortConfig?.type === 'priority') {
    const priority = fieldSortConfig?.priority;

    return (a: any, b: any) => {
      const firstValue = a?.[key];
      const secondValue = b?.[key];

      const firstPriority = priority?.[firstValue] ?? Infinity;
      const secondPriority = priority?.[secondValue] ?? Infinity;

      return firstPriority - secondPriority;
    };
  }

  // Default sorting logic
  return (a: any, b: any) => {
    const firstValue = a?.[key];
    const secondValue = b?.[key];

    if (firstValue === undefined && secondValue === undefined) return 0;
    if (firstValue === undefined) return 1;
    if (secondValue === undefined) return -1;

    if (typeof firstValue === typeof secondValue) {
      if (typeof firstValue === 'string') {
        return firstValue.localeCompare(secondValue);
      }
      if (typeof firstValue === 'number') {
        return firstValue - secondValue;
      }
    }
    return 0;
  };
};

const SalesTable = ({
  records,
  fieldMeta,
  loading,
  fieldsToRender,
  pagination,
}: SalesTableProps) => {
  const [sortedInfo, setSortedInfo] = useState<SorterResult<any> | null>({});

  const fieldMetaWithIdKeys = useMemo(
    () => createFieldMetaMap(fieldMeta),
    [fieldMeta],
  );

  const tableHeaders = useMemo(() => {
    if (!records?.length || !fieldsToRender) return [];

    const filteredFields = Object.entries(fieldMetaWithIdKeys)?.filter(
      ([key]) => key in fieldsToRender,
    );

    const sortedFields = sortFieldsByRenderOrder(
      filteredFields,
      fieldsToRender,
    );

    return sortedFields.map(
      ([key, meta]): TableColumnType<any> => ({
        title: meta.name,
        dataIndex: key,
        key,
        width: fieldsToRender[key]?.width || 200,
        sorter: createSorterWithConfig(fieldsToRender[key]?.sort, key) as any,
        sortOrder: sortedInfo?.columnKey === key ? sortedInfo.order : null,
        render: (value: string) => (
          <FieldRenderer name={meta.name} value={value} fieldMeta={fieldMeta} />
        ),
      }),
    );
  }, [records, fieldMetaWithIdKeys, fieldsToRender, fieldMeta, sortedInfo]);

  const dataSource = useMemo(() => {
    if (!records?.length) return [];

    return records.map((record) => {
      const transformedRecord: Record<string, any> = { key: record.id };

      Object.entries(record?.fields).forEach(([key, value]) => {
        const fieldId = fieldMeta?.[key]?.id;
        if (fieldId) {
          transformedRecord[fieldId] = value;
        }
      });

      return transformedRecord;
    });
  }, [records, fieldMeta]);

  const handleTableChange: TableProps['onChange'] = (__, _, sorter) => {
    setSortedInfo(sorter as SorterResult<any>);
  };

  useEffect(() => {
    if (!fieldsToRender) return;

    const columnWithPrioritySort = Object.entries(fieldsToRender)?.find(
      ([, value]) => value?.sort?.type === 'priority' && value?.sort?.priority,
    )?.[0];

    if (!columnWithPrioritySort) return;

    setSortedInfo({
      columnKey: columnWithPrioritySort,
      order: 'ascend',
    });
  }, [fieldsToRender]);

  return (
    <TableComponent
      className="max-w-full"
      tableColumns={dataSource}
      tableHeader={tableHeaders}
      loading={loading}
      onChange={handleTableChange}
      pagination={pagination}
    />
  );
};

export default SalesTable;
